// src/contexts/StoreContext.js
import React, { createContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { PAYMENT_V2_STORE_ID } from '../utils/paymentV2StoreGate';

export const StoreContext = createContext({
  store: null,
  loading: true,
});

export function StoreProvider({ children }) {
  const [store, setStore] = useState(() => {
    try {
      const cached = localStorage.getItem('storeDetails');
      if (!cached) return null;
      const value = JSON.parse(cached);
      // A cached browser value is display-only and must never reopen checkout.
      // The live Firestore snapshot below is the only client-side gate source;
      // the Python quote service independently enforces the same server field.
      return { ...value, paymentV2Enabled: false };
    } catch {
      return null;
    }
  });
  // A cached store is display-only and is forced disabled above. Keep the
  // authoritative loading state true until the live Firestore listener
  // succeeds or fails so route guards cannot mistake cache presence for a
  // current payment decision.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'stores', PAYMENT_V2_STORE_ID), (snap) => {
      if (snap.exists()) {
        const storeData = { id: snap.id, ...snap.data() };
        setStore(storeData);
        try {
          localStorage.setItem('storeDetails', JSON.stringify(storeData));
        } catch {}
      } else {
        setStore(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Store listener error:', error);
      // A previously enabled snapshot must not survive loss of the
      // authoritative listener as a reusable checkout gate.
      setStore(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <StoreContext.Provider value={{ store, loading }}>
      {children}
    </StoreContext.Provider>
  );
}
